// Story 1.24 — TanStack useMutation for POST /api/v1/portfolios/:id/apply.
//
// Translates backend error codes (404 / 409 with three sub-codes /
// 400 / 429) into UX-friendly toast messages. Invalidates the browse
// list + the my-applications list on success so the SPA re-renders
// the new `already_applied=true` state without a manual refetch.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import { attachRequestIdToScope } from "../../lib/api-client";

export type SubmitPortfolioApplicationInput = {
  portfolio_id: string;
  optional_drop_specific_hello_text?: string | null;
};

export type SubmitPortfolioApplicationData = {
  portfolio_application_id: string;
  status: string;
  submitted_at: string;
};

export type SubmitPortfolioApplicationResponse = {
  data: SubmitPortfolioApplicationData;
};

export class SubmitPortfolioApplicationError extends Error {
  readonly code: string;
  readonly status: number;
  readonly currentStatus?: string;
  readonly portfolioId?: string;

  constructor(args: {
    code: string;
    message: string;
    status: number;
    currentStatus?: string;
    portfolioId?: string;
  }) {
    super(args.message);
    this.name = "SubmitPortfolioApplicationError";
    this.code = args.code;
    this.status = args.status;
    this.currentStatus = args.currentStatus;
    this.portfolioId = args.portfolioId;
  }
}

async function submitPortfolioApplication(
  input: SubmitPortfolioApplicationInput,
): Promise<SubmitPortfolioApplicationResponse> {
  const body: Record<string, unknown> = {};
  if (
    input.optional_drop_specific_hello_text &&
    input.optional_drop_specific_hello_text.length > 0
  ) {
    body.optional_drop_specific_hello_text =
      input.optional_drop_specific_hello_text;
  }
  const response = await fetch(
    `/api/v1/portfolios/${encodeURIComponent(input.portfolio_id)}/apply`,
    {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    let payload: Record<string, unknown> = {};
    try {
      payload = (await response.json()) as Record<string, unknown>;
    } catch {
      // body not JSON
    }
    const err = (payload?.error ?? {}) as Record<string, unknown>;
    throw new SubmitPortfolioApplicationError({
      code: (err.code as string) ?? "UNKNOWN",
      message:
        (err.message as string) ??
        `Application failed (HTTP ${response.status})`,
      status: response.status,
      currentStatus: err.current_status as string | undefined,
      portfolioId: err.portfolio_id as string | undefined,
    });
  }
  return (await response.json()) as SubmitPortfolioApplicationResponse;
}

export function useSubmitPortfolioApplication() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: submitPortfolioApplication,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["creator-portfolios", "recruiting"],
      });
      queryClient.invalidateQueries({
        queryKey: ["creator-portfolios", "my-applications"],
      });
    },
    onError: (error) => {
      if (error instanceof SubmitPortfolioApplicationError) {
        switch (error.code) {
          case "PORTFOLIO_NOT_FOUND":
            toast.error("This portfolio is no longer available.");
            break;
          case "PORTFOLIO_NOT_RECRUITING":
            toast.error(
              `This portfolio is no longer accepting applications (status: ${error.currentStatus ?? "?"}).`,
            );
            break;
          case "PORTFOLIO_RECRUITING_DEADLINE_PASSED":
            toast.error("The recruiting window for this portfolio has closed.");
            break;
          case "PORTFOLIO_APPLICATION_ALREADY_ACTIVE":
            toast.error(
              "You already have a pending or approved application for this portfolio.",
            );
            break;
          case "RATE_LIMITED":
            toast.error(
              "You've hit the 5-applications-per-day limit. Try again later.",
            );
            break;
          case "CREATOR_PROFILE_FORBIDDEN":
            toast.error("Approved-creator only.");
            break;
          default:
            toast.error(error.message);
        }
      } else {
        toast.error("Something went wrong. Try again.");
      }
    },
  });
}
